import axios from 'axios';
import React, { useEffect, useRef, useState } from 'react';
import { FaBook, FaChevronLeft, FaChevronRight, FaClipboardList, FaShoppingCart, FaUser } from "react-icons/fa";
import { Link } from 'react-router-dom';
import { API } from '../../../utils/constants';
import { useAuth } from '../../../utils/context';

const DashboardOverview = () => {
    const [auth] = useAuth();
    const [animate, setAnimate] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [activeTab, setActiveTab] = useState('all'); // 'all', 'mock', 'practice'
    const [hoveredData, setHoveredData] = useState(null);
    const [showLeftScroll, setShowLeftScroll] = useState(false);
    const [showRightScroll, setShowRightScroll] = useState(false);
    const subjectsContainerRef = useRef(null);
    const barsContainerRef = useRef(null);

    // Fetch user stats from API
    useEffect(() => {
        const fetchUserStats = async () => {
            try {
                setIsLoading(true);
                const response = await axios.get(
                    `${API}/scores/user-stats/${auth.user?._id}`,
                    {
                        headers: {
                            Authorization: `Bearer ${auth.accessToken.token}`,
                        },
                    }
                );

                if (response.data.success) {
                    setUserData(response.data);
                }
            } catch (error) {
                console.error("Error fetching user stats:", error);
            } finally {
                setIsLoading(false);
            }
        };

        if (auth.user?._id) {
            fetchUserStats();
        }
    }, [auth.user?._id, auth.accessToken?.token]);

    // Check if horizontal scrolling is needed
    useEffect(() => {
        const checkScrollability = () => {
            if (subjectsContainerRef.current) {
                const container = subjectsContainerRef.current;
                setShowRightScroll(container.scrollWidth > container.clientWidth);
            }

            if (barsContainerRef.current) {
                const container = barsContainerRef.current;
                const isScrollable = container.scrollWidth > container.clientWidth;
                setShowRightScroll(isScrollable);
            }
        };

        checkScrollability();
        window.addEventListener('resize', checkScrollability);
        return () => window.removeEventListener('resize', checkScrollability);
    }, [userData]);

    // Handle scroll for subjects container
    const handleSubjectsScroll = (direction) => {
        if (subjectsContainerRef.current) {
            const container = subjectsContainerRef.current;
            const scrollAmount = direction === 'left' ? -300 : 300;
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' });

            // Update scroll indicators after scroll
            setTimeout(() => {
                setShowLeftScroll(container.scrollLeft > 0);
                setShowRightScroll(container.scrollLeft < (container.scrollWidth - container.clientWidth));
            }, 300);
        }
    };


    // Format data for the dashboard
    const formatDashboardData = () => {
        if (!userData || !userData.userStats) {
            return {
                subjects: [],
                overallPerformance: { score: 0, rating: 'N/A' },
                studentCount: 0,
                userRank: 0
            };
        }

        const { userStats } = userData;

        // Transform topics data for the dashboard
        const subjects = userStats.topicsData.map(topic => {
            // Count unique mock tests by their test number
            const uniqueMockTests = new Set();
            const mockAttempts = topic.attempts?.filter(attempt => {
                if (attempt.testType === 'mock' && !uniqueMockTests.has(attempt.testNumber)) {
                    uniqueMockTests.add(attempt.testNumber);
                    return true;
                }
                return false;
            }) || [];

            // Count unique practice tests by their test number
            const uniquePracticeTests = new Set();
            const practiceAttempts = topic.attempts?.filter(attempt => {
                if (attempt.testType === 'practice' && !uniquePracticeTests.has(attempt.testNumber)) {
                    uniquePracticeTests.add(attempt.testNumber);
                    return true;
                }
                return false;
            }) || [];

            // Calculate scores based on unique attempts
            const mockScore = mockAttempts.length > 0
                ? mockAttempts.reduce((sum, attempt) => sum + attempt.score, 0) / mockAttempts.length
                : 0;

            const practiceScore = practiceAttempts.length > 0
                ? practiceAttempts.reduce((sum, attempt) => sum + attempt.score, 0) / practiceAttempts.length
                : 0;

            // Get overall score (average of all unique attempts)
            const allUniqueAttempts = [...mockAttempts, ...practiceAttempts];
            const overallScore = allUniqueAttempts.length > 0
                ? allUniqueAttempts.reduce((sum, attempt) => sum + attempt.score, 0) / allUniqueAttempts.length
                : 0;

            // Get recent attempts (up to 3), prioritizing the most recent attempt for each unique test
            const recentTestMap = new Map();

            // Group attempts by test number and keep the most recent
            topic.attempts?.forEach(attempt => {
                if (!recentTestMap.has(attempt.testNumber) ||
                    attempt.attemptDate > recentTestMap.get(attempt.testNumber).attemptDate) {
                    recentTestMap.set(attempt.testNumber, attempt);
                }
            });

            // Get the most recent 3 tests
            const recentAttempts = Array.from(recentTestMap.values())
                .sort((a, b) => new Date(b.attemptDate) - new Date(a.attemptDate))
                .slice(0, 3)
                .map(attempt => attempt.score);

            return {
                name: topic.topicName,
                score: overallScore,
                results: {
                    pass: mockScore,
                    fail: 100 - mockScore - (uniqueMockTests.size > 0 ? 0 : 100),
                    notAttended: practiceScore
                },
                mockScore: mockScore,
                practiceScore: practiceScore,
                recentAttempts: recentAttempts,
                totalAttempts: uniqueMockTests.size + uniquePracticeTests.size,
                mockAttempts: uniqueMockTests.size,
                practiceAttempts: uniquePracticeTests.size,
                // Store the sets of unique test numbers for reference if needed
                uniqueMockTestNumbers: Array.from(uniqueMockTests),
                uniquePracticeTestNumbers: Array.from(uniquePracticeTests)
            };
        });

        // Sort subjects by score (highest first)
        subjects.sort((a, b) => b.score - a.score);

        // Format overall performance
        const overallScore = userStats.overallStats?.averageScore || 0;
        let rating = 'Poor';
        if (overallScore >= 80) rating = 'Excellent';
        else if (overallScore >= 70) rating = 'Very Good';
        else if (overallScore >= 60) rating = 'Good';
        else if (overallScore >= 50) rating = 'Average';

        return {
            subjects,
            overallPerformance: {
                score: overallScore,
                rating
            },
            studentCount: userStats.instituteRanking?.totalStudents || 0,
            userRank: userStats.instituteRanking?.rank
        };
    };
    const dashboardData = formatDashboardData();
    const allSubjects = dashboardData.subjects;

    useEffect(() => {
        if (!isLoading) {
            // Use a slight delay to ensure DOM is ready for animations
            const timer = setTimeout(() => {
                setAnimate(true);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    if (isLoading) {
        return <div className="flex justify-center items-center h-screen">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-solid border-blue-500"></div>
        </div>;
    }

    // Check if we have subjects
    const hasSubjects = auth?.user?.selectedSubjects && auth?.user?.selectedSubjects.length > 0;

    // Check if we have any attempts across all subjects
    const hasAttempts = dashboardData.subjects.some(subject => subject.totalAttempts > 0);

    return (
        <div className="font-sans mx-auto px-2 md:px-4">
            {/* Subjects Container with horizontal scroll */}
            <div className="relative mb-6">
                {/* Left scroll button */}
                {showLeftScroll && (
                    <button
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-md p-2 md:flex items-center justify-center hidden"
                        onClick={() => handleSubjectsScroll('left')}
                    >
                        <FaChevronLeft className="text-blue-500" />
                    </button>
                )}

                {/* Subjects container */}
                <div
                    ref={subjectsContainerRef}
                    className="flex overflow-x-auto scrollbar-hide gap-4 md:gap-6 pb-2 snap-x"
                    onScroll={(e) => {
                        const container = e.currentTarget;
                        setShowLeftScroll(container.scrollLeft > 0);
                        setShowRightScroll(container.scrollLeft < (container.scrollWidth - container.clientWidth - 5));
                    }}
                >
                    {!hasSubjects ? (
                        // No subjects case
                        <div className="min-w-[280px] md:min-w-[320px] flex-none snap-start">
                            <NoPlanCard animate={animate} />
                        </div>
                    ) : allSubjects.length === 0 || !hasAttempts ? (
                        // Has subjects but no attempts case
                        <div className="min-w-[280px] md:min-w-[320px] flex-none snap-start">
                            <NoAttemptsCard animate={animate} />
                        </div>
                    ) : (
                        // Normal case: render all subjects
                        allSubjects.map((subject, index) => (
                            <div key={index} className="min-w-[280px] md:min-w-[320px] flex-none snap-start">
                                <SubjectCard
                                    subject={subject}
                                    animate={animate}
                                    index={index}
                                />
                            </div>
                        ))
                    )}
                </div>

                {/* Right scroll button */}
                {showRightScroll && (
                    <button
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full shadow-md p-2 md:flex items-center justify-center hidden"
                        onClick={() => handleSubjectsScroll('right')}
                    >
                        <FaChevronRight className="text-blue-500" />
                    </button>
                )}
            </div>

            <div className="flex lg:flex-row flex-col w-full gap-4 md:gap-6">
                <div className="lg:w-[70%] w-full">
                    {allSubjects.length > 0 ? (
                        <SubjectResultsGraph
                            subjects={dashboardData.subjects}
                            animate={animate}
                            activeTab={activeTab}
                            setActiveTab={setActiveTab}
                            hoveredData={hoveredData}
                            setHoveredData={setHoveredData}
                            needsHorizontalScroll={allSubjects.length > 5}
                        />
                    ) : (
                        <div className="bg-white rounded-2xl p-4 md:p-6 shadow-md w-full h-full flex items-center justify-center">
                            <p className="text-gray-500 text-center">No subject data available.</p>
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-4 lg:w-[30%] w-full md:gap-6">
                    <PerformanceCard
                        performance={dashboardData.overallPerformance}
                        animate={animate}
                    />
                    <StudentCountCard
                        studentCount={dashboardData.studentCount}
                        animate={animate}
                        userRank={dashboardData.userRank}
                    />
                </div>
            </div>
        </div>
    );
};

const NoPlanCard = ({ animate }) => {
    return (
        <div
            className={`bg-white rounded-2xl p-4 md:p-6 shadow-md w-full transition-all duration-700 ease-in-out transform ${animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
            <div className="flex flex-col items-center justify-center gap-4 py-2">
                <div className="bg-blue-100 w-16 h-16 flex items-center justify-center rounded-full">
                    <FaShoppingCart className="text-blue-500 text-3xl" />
                </div>
                <h3 className="text-xl font-medium my-0 text-gray-800 text-center">No Plans Yet</h3>
                <p className="text-gray-600 text-center mb-2">Purchase a plan to start your learning journey</p>
                <Link
                    to="/purchase"
                    className="bg-blue-500 text-white font-medium px-6 py-2 rounded-md text-center hover:bg-blue-600 transition-colors w-full md:w-2/3"
                >
                    Buy Plans
                </Link>
            </div>
        </div>
    );
};

const NoAttemptsCard = ({ animate }) => {
    return (
        <div
            className={`bg-white rounded-2xl p-4 md:p-6 shadow-md w-full transition-all duration-700 ease-in-out transform ${animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
            <div className="flex flex-col items-center justify-center gap-4 py-2">
                <div className="bg-blue-100 w-16 h-16 flex items-center justify-center rounded-full">
                    <FaClipboardList className="text-blue-500 text-3xl" />
                </div>
                <h3 className="text-xl my-0 font-medium text-gray-800 text-center">Start Practice</h3>
                <p className="text-gray-600 text-center mb-2">Take tests to see your performance here</p>
                <Link
                    to="/"
                    className="bg-blue-500 text-white font-medium px-6 py-2 rounded-md text-center hover:bg-blue-600 transition-colors w-full md:w-2/3"
                >
                    Attempt Tests
                </Link>
            </div>
        </div>
    );
};

const SubjectCard = ({ subject, animate, index }) => {
    // Maximum number of tests
    const MAX_TESTS = 15;

    // Generate graph data based on recent attempts or default values
    const graphData = [30, 40, 60, 45, 80]; // Default graph values if no data

    // Ensure we have exactly 5 data points for the graph
    while (graphData.length < 5) {
        graphData.push(30 + Math.floor(Math.random() * 50)); // Add random values between 30-80
    }

    // Take only the last 5 values if we have more
    const displayGraphData = graphData.slice(-5);

    // Format subject name to title case with spaces instead of underscores
    const formatSubjectName = (name) => {
        // Replace underscores with spaces and capitalize each word
        return name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    return (
        <div
            className={`bg-white rounded-2xl p-4 md:p-6 shadow-md w-full transition-all duration-700 ease-in-out transform ${animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: `${index * 150}ms` }}
        >
            {/* Subject Icon and Label */}
            <div>
                <div className="bg-blue-100 w-8 h-8 flex items-center justify-center rounded-lg mb-1">
                    <FaBook className='text-blue-400' />
                </div>
                <div className="text-gray-800 font-medium truncate">
                    {formatSubjectName(subject.name)}
                </div>
            </div>

            {/* Score and Graph */}
            <div className="flex justify-between items-end mt-2">
                <div className="text-2xl md:text-[40px] font-bold">
                    {subject.score.toFixed(1)}
                </div>

                <div className="flex items-end h-16 md:h-20 gap-[2px]">
                    {displayGraphData.map((value, i) => (
                        <div
                            key={i}
                            className={`w-2 md:w-4 rounded-md transition-all duration-700 ease-in-out ${i === 0 || i === 2 ? 'bg-blue-200' :
                                i === 4 ? 'bg-blue-600' : 'bg-blue-400'
                                }`}
                            style={{
                                height: animate ? `${value}%` : '0%',
                                transitionDelay: `${(index * 150) + (i * 80)}ms`
                            }}
                        ></div>
                    ))}
                </div>
            </div>

            {/* Additional stats - Now with unique test counts */}
            <div className="mt-3 flex flex-col gap-1">
                <div className="flex justify-between text-xs">
                    <div className="text-gray-700 font-medium">Mock Tests:</div>
                    <div className="text-gray-600">
                        {subject.mockAttempts} of {MAX_TESTS}
                        <span className={subject.mockScore > 0 ? "text-green-500 ml-1" : "text-gray-400 ml-1"}>
                            ({subject.mockScore.toFixed(1)}%)
                        </span>
                    </div>
                </div>
                <div className="flex justify-between text-xs">
                    <div className="text-gray-700 font-medium">Practice Tests:</div>
                    <div className="text-gray-600">
                        {subject.practiceAttempts} of {MAX_TESTS}
                        <span className={subject.practiceScore > 0 ? "text-green-500 ml-1" : "text-gray-400 ml-1"}>
                            ({subject.practiceScore.toFixed(1)}%)
                        </span>
                    </div>
                </div>
                <div className="flex justify-between text-xs">
                    <div className="text-gray-700 font-medium">Total Unique Tests:</div>
                    <div className="text-gray-600">{subject.totalAttempts}</div>
                </div>

                {/* Take Test Button removed for all devices */}
            </div>
        </div>
    );
};

const SubjectResultsGraph = ({ subjects, animate, activeTab, setActiveTab, hoveredData, setHoveredData, needsHorizontalScroll }) => {
    // Maximum value for scaling the graph
    const maxValue = 100;
    const horizontalScrollRef = useRef(null);
    const [showLeftScroll, setShowLeftScroll] = useState(false);
    const [showRightScroll, setShowRightScroll] = useState(false);

    // Check if horizontal scrolling is needed
    useEffect(() => {
        const checkScrollability = () => {
            if (horizontalScrollRef.current && needsHorizontalScroll) {
                const container = horizontalScrollRef.current;
                setShowRightScroll(container.scrollWidth > container.clientWidth);
            }
        };

        checkScrollability();
        window.addEventListener('resize', checkScrollability);
        return () => window.removeEventListener('resize', checkScrollability);
    }, [subjects, needsHorizontalScroll]);

    // Handle scroll for horizontal container
    const handleHorizontalScroll = (direction) => {
        if (horizontalScrollRef.current) {
            const container = horizontalScrollRef.current;
            const scrollAmount = direction === 'left' ? -200 : 200;
            container.scrollBy({ left: scrollAmount, behavior: 'smooth' });

            // Update scroll indicators after scroll
            setTimeout(() => {
                setShowLeftScroll(container.scrollLeft > 0);
                setShowRightScroll(container.scrollLeft < (container.scrollWidth - container.clientWidth));
            }, 300);
        }
    };

    // Filter subjects based on active tab
    const getFilteredData = () => {
        return subjects.map(subject => {
            if (activeTab === 'mock') {
                return {
                    ...subject,
                    results: {
                        pass: subject.mockScore,
                        fail: 100 - subject.mockScore - (subject.mockScore > 0 ? 0 : 100),
                        notAttended: 0
                    }
                };
            } else if (activeTab === 'practice') {
                return {
                    ...subject,
                    results: {
                        pass: subject.practiceScore,
                        fail: 0,
                        notAttended: 100 - subject.practiceScore - (subject.practiceScore > 0 ? 0 : 100)
                    }
                };
            }
            return subject;
        });
    };

    const filteredSubjects = getFilteredData();

    return (
        <div className="bg-white rounded-2xl md:pl-10 pl-6 md:pt-3 pb-10 pt-1 md:pr-5 pr-2 shadow-md w-full h-full">
            <div className="flex justify-between items-center mb-3">
                <h2 className="text-gray-800 font-medium mt-0">Subject-Based Results</h2>

                {/* Tabs */}
                {/* <div className="flex space-x-2 mr-4">
          <button 
            className={`px-3 py-1 text-sm border-[1px] shadow-lg rounded-md transition-colors ${activeTab === 'all' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-blue-200'}`}
            onClick={() => setActiveTab('all')}
          >
            All
          </button>
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${activeTab === 'mock' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-blue-200'}`}
            onClick={() => setActiveTab('mock')}
          >
            Mock
          </button>
          <button 
            className={`px-3 py-1 text-sm rounded-md transition-colors ${activeTab === 'practice' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-blue-200'}`}
            onClick={() => setActiveTab('practice')}
          >
            Practice
          </button>
        </div> */}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 md:gap-6 mb-4 md:mb-6">
                <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-blue-600 rounded-sm"></div>
                    <span className="text-xs md:text-sm text-gray-700">
                        {activeTab === 'all' || activeTab === 'mock' ? 'Mock Test Score' : 'Practice Score'}
                    </span>
                </div>
                {(activeTab === 'all' || activeTab === 'mock') && (
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-purple-300 rounded-sm"></div>
                        <span className="text-xs md:text-sm text-gray-700">Overall Score</span>
                    </div>

                )}
                {(activeTab === 'all' || activeTab === 'practice') && (
                    <div className="flex items-center gap-2">
                        <div className="w-4 h-4 bg-blue-200 rounded-sm"></div>
                        <span className="text-xs md:text-sm text-gray-700">Practice Score</span>
                    </div>
                )}
            </div>

            {/* Tooltip for data */}
            {hoveredData && (
                <div
                    className="absolute z-10 bg-white p-3 rounded-lg shadow-lg border border-gray-200 text-sm pointer-events-none animate-fadeIn"
                    style={{
                        left: `${hoveredData.x}px`,
                        top: `${hoveredData.y}px`,
                        transform: 'translate(-50%, -100%)'
                    }}
                >
                    <div className="font-bold mb-1">{hoveredData.subject}</div>
                    {activeTab === 'all' || activeTab === 'mock' ? (
                        <div>Mock Score: {hoveredData.pass.toFixed(1)}%</div>
                    ) : null}
                    {activeTab === 'all' || activeTab === 'practice' ? (
                        <div>Practice Score: {hoveredData.notAttended.toFixed(1)}%</div>
                    ) : null}
                    <div>Overall Score: {hoveredData.total.toFixed(1)}%</div>
                </div>
            )}

            {/* Graph Container */}
            <div className="relative">
                {/* Horizontal scroll buttons (only when needed) */}
                {needsHorizontalScroll && (
                    <>
                        {showLeftScroll && (
                            <button
                                className="absolute left-0 top-1/2 z-10 bg-white rounded-full shadow-md p-2 flex items-center justify-center"
                                onClick={() => handleHorizontalScroll('left')}
                                style={{ transform: 'translateY(-50%)' }}
                            >
                                <FaChevronLeft className="text-blue-500" />
                            </button>
                        )}
                        {showRightScroll && (
                            <button
                                className="absolute right-0 top-1/2 z-10 bg-white rounded-full shadow-md p-2 flex items-center justify-center"
                                onClick={() => handleHorizontalScroll('right')}
                                style={{ transform: 'translateY(-50%)' }}
                            >
                                <FaChevronRight className="text-blue-500" />
                            </button>
                        )}
                    </>
                )}

                {/* Scrollable container for horizontal scrolling */}
                <div
                    ref={horizontalScrollRef}
                    className={`${needsHorizontalScroll ? 'overflow-x-auto scrollbar-hide' : ''}`}
                    onScroll={needsHorizontalScroll ? (e) => {
                        const container = e.currentTarget;
                        setShowLeftScroll(container.scrollLeft > 0);
                        setShowRightScroll(container.scrollLeft < (container.scrollWidth - container.clientWidth - 5));
                    } : undefined}
                >
                    <div className="h-60 md:h-80 min-w-full" style={{ width: needsHorizontalScroll ? `${Math.max(100, subjects.length * 15)}%` : '100%' }}>
                        {/* Horizontal Lines and Labels */}
                        {[0, 20, 40, 60, 80, 100].map((value) => (
                            <div
                                key={value}
                                className="absolute w-full border-gray-200 flex items-center"
                                style={{ top: `${100 - value}%`, height: '1px', borderTop: '0.5px solid' }}
                            >
                                <span className="text-xs text-gray-500 -ml-6">{value}</span>
                            </div>
                        ))}

                        {/* Bar Groups */}
                        <div className="absolute bottom-0 w-full h-full flex justify-between px-2 md:px-4">
                            {filteredSubjects.map((subject, index) => (
                                <div
                                    key={index}
                                    className="flex items-end justify-center gap-[1px] md:gap-[2px] h-full relative"
                                    style={{ width: needsHorizontalScroll ? `${100 / filteredSubjects.length}%` : `${100 / Math.min(filteredSubjects.length, 10)}%` }}
                                >
                                    {/* Overall Bar */}
                                    {(activeTab === 'all' || activeTab === 'mock') && (
                                        <div
                                            className="w-2 md:w-5 bg-purple-300 transition-all rounded-md duration-1000 ease-out cursor-pointer"
                                            style={{
                                                height: animate ? `${(subject.score / maxValue) * 100}%` : '0%',
                                                transitionDelay: `${300 + (index * 100)}ms`
                                            }}
                                            onMouseEnter={(e) => {
                                                setHoveredData({
                                                    subject: subject.name,
                                                    pass: subject.results.pass,
                                                    fail: subject.results.fail,
                                                    notAttended: subject.results.notAttended,
                                                    total: subject.score,
                                                    x: e.clientX,
                                                    y: e.clientY - 20
                                                });
                                            }}
                                            onMouseLeave={() => setHoveredData(null)}
                                        ></div>
                                    )}

                                    {/* Mock Bar */}
                                    <div
                                        className="w-2 md:w-5 bg-blue-600 rounded-md transition-all duration-1000 ease-out cursor-pointer"
                                        style={{
                                            height: animate ? `${(subject.results.pass / maxValue) * 100}%` : '0%',
                                            transitionDelay: `${400 + (index * 100)}ms`
                                        }}
                                        onMouseEnter={(e) => {
                                            setHoveredData({
                                                subject: subject.name,
                                                pass: subject.results.pass,
                                                fail: subject.results.fail,
                                                notAttended: subject.results.notAttended,
                                                total: subject.score,
                                                x: e.clientX,
                                                y: e.clientY - 20
                                            });
                                        }}
                                        onMouseLeave={() => setHoveredData(null)}
                                    ></div>

                                    {/*  Practice Bar */}
                                    {(activeTab === 'all' || activeTab === 'practice') && (
                                        <div
                                            className="w-2 md:w-5 bg-blue-200 rounded-md transition-all duration-1000 ease-out cursor-pointer"
                                            style={{
                                                height: animate ? `${(subject.results.notAttended / maxValue) * 100}%` : '0%',
                                                transitionDelay: `${500 + (index * 100)}ms`
                                            }}
                                            onMouseEnter={(e) => {
                                                setHoveredData({
                                                    subject: subject.name,
                                                    pass: subject.results.pass,
                                                    fail: subject.results.fail,
                                                    notAttended: subject.results.notAttended,
                                                    total: subject.score,
                                                    x: e.clientX,
                                                    y: e.clientY - 20
                                                });
                                            }}
                                            onMouseLeave={() => setHoveredData(null)}
                                        ></div>
                                    )}

                                    {/* Subject Label */}
                                    <div className="absolute -bottom-6 w-full text-xs text-gray-700 text-center whitespace-nowrap overflow-hidden text-ellipsis px-1">
                                        {subject.name}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const PerformanceCard = ({ performance, animate }) => {
    // Calculate progress circle properties
    const radius = 40;
    const circumference = 2 * Math.PI * radius;
    const maxScore = 100; // Changed from 500 to 100 to match the API percentage scores
    const progressPercentage = performance.score / maxScore;
    const progressOffset = circumference * (1 - progressPercentage);

    return (
        <div className={`bg-white rounded-2xl max-md:p-4 p-2 px-3 md:pt-3 shadow-md w-full transition-all duration-500 ease-in-out transform ${animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '300ms' }}>
            <h2 className="text-gray-800 font-medium mt-0 mb-1">Overall Performance</h2>

            <div className="flex justify-center">
                <div className="relative w-24 h-24 md:w-32 md:h-32">
                    {/* Background Circle */}
                    <svg className="w-full h-full -rotate-90">
                        <circle
                            cx="50%"
                            cy="50%"
                            r={radius}
                            fill="none"
                            stroke="#f3f4f6"
                            strokeWidth="10"
                        />
                        {/* Progress Circle */}
                        <circle
                            cx="50%"
                            cy="50%"
                            r={radius}
                            fill="none"
                            stroke="#4361ee"
                            strokeWidth="10"
                            strokeDasharray={circumference}
                            strokeDashoffset={animate ? progressOffset : circumference}
                            className="transition-all duration-1500 ease-out"
                            style={{ transitionDelay: '500ms' }}
                        />
                    </svg>

                    {/* Center Content */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className={`text-xl md:text-2xl font-bold transition-all duration-1000 ${animate ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '800ms' }}>
                            {performance.score ? performance.score.toFixed(2) : "N/A"}
                        </div>
                        <div className="text-xs text-gray-500">{performance.rating}</div>
                    </div>
                </div>
            </div>

            <div className="flex justify-center w-1/2 mx-auto mt-1">
                <Link to={"/profile/performance"}
                    className="bg-blue-100 text-blue-500 text-center font-bold px-6 py-2 rounded-md text-sm hover:bg-blue-200 transition-colors w-full"
                >
                    Details
                </Link>
            </div>
        </div>
    );
};

const StudentCountCard = ({ studentCount, userRank, animate }) => {
    return (
        <div className={`bg-white rounded-2xl max-md:p-4 p-2 px-3 md:pt-3 shadow-md w-full transition-all duration-500 ease-in-out transform ${animate ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '300ms' }}>
            <h2 className="text-gray-800 font-medium mt-0 mb-1">Student Count</h2>

            {/* Gauge Chart */}
            <div className="flex justify-center mb-3">
                <div className="relative w-full h-12 md:h-14">
                    <svg className="w-full h-full" viewBox="0 0 100 50">
                        {/* Gauge background */}
                        <path
                            d="M10,50 A40,40 0 0,1 90,50"
                            fill="none"
                            stroke="#f3f4f6"
                            strokeWidth="1"
                        />

                        {/* Gauge Lines */}
                        {Array.from({ length: 50 }).map((_, i) => (
                            <line
                                key={i}
                                x1="50"
                                y1="50"
                                x2={50 + 40 * Math.cos(Math.PI * (0.8 + i * 0.02))}
                                y2={50 + 40 * Math.sin(Math.PI * (0.8 + i * 0.02))}
                                stroke="#4361ee"
                                strokeWidth="1"
                                strokeOpacity={animate ? 1 : 0}
                                className="transition-opacity duration-50"
                                style={{ transitionDelay: `${600 + i * 30}ms` }}
                            />
                        ))}
                    </svg>
                </div>
            </div>

            {/* User Icon */}
            <div className="flex justify-center pb-2 -mt-1">
                <div className={`bg-blue-200 rounded-full p-2 w-8 h-8 flex items-center justify-center transition-all duration-500 transform ${animate ? 'scale-100' : 'scale-0'}`} style={{ transitionDelay: '1100ms' }}>
                    <FaUser className='text-blue-500' />
                </div>
            </div>

            {/* Count and Label */}
            <div className="flex flex-col items-center justify-center mt-1">
                <div className={`text-xl font-bold transition-all duration-700 ${animate ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '1300ms' }}>
                    {studentCount}
                </div>
                <div className="text-xs text-gray-500">Students</div>

                {/* Rank information */}
                <div className={`mt-1 text-sm font-semibold ${userRank === 1 ? 'text-yellow-500' : userRank === 2 ? 'text-gray-500' : userRank === 3 ? 'text-amber-700' : 'text-blue-500'} transition-all duration-700 ${animate ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '1400ms' }}>
                    Rank: #{userRank}
                </div>
            </div>

            {/* Details Button */}
            {/* <div className="flex justify-center w-1/2 mx-auto mt-4">
        <Link to={"/profile/students-list"}
          className="bg-blue-100 text-blue-500 text-center font-bold px-6 py-2 rounded-md text-sm hover:bg-blue-200 transition-colors w-full"
        >
          Details
        </Link>
      </div> */}
        </div>
    );
};

export default DashboardOverview;